import React, { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { RefreshCw, Send, ShieldCheck, Loader2, Home } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

export const HometobSyncPanel = () => {
  const [status, setStatus] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState('');

  const load = async () => {
    try {
      const [s, r] = await Promise.all([
        api.get('/admin/hometob/status', { timeout: 15000 }),
        api.get('/admin/hometob/report?days=7', { timeout: 15000 }),
      ]);
      setStatus(s.data);
      setReport(r.data);
    } catch {
      setStatus(null);
    }
  };

  useEffect(() => { load(); }, []);

  const pushAll = async () => {
    setBusy('push');
    try {
      await api.post('/admin/hometob/push-all');
      toast.success('Reinvio completo avviato: aggiorna tra qualche minuto');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel reinvio');
    } finally { setBusy(''); }
  };

  const checkAlignment = async () => {
    setBusy('align');
    try {
      const r = await api.get('/admin/hometob/alignment', { timeout: 120000 });
      const d = r.data;
      toast.success(`Allineamento: ${d.aligned} identiche · ${d.mismatched.length} diverse · ${d.missing_on_hometob.length} mancanti · ${d.extra_on_hometob.length} in più`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore verifica allineamento');
    } finally { setBusy(''); }
  };

  if (!status) return null;
  const t = report?.totals || {};
  const failures = report?.failures || [];

  return (
    <Card className="p-6 border-0 shadow-lg bg-white/90 backdrop-blur-sm" data-testid="hometob-sync-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-indigo-900 to-indigo-700">
            <Home className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Sincronizzazione HomeToB.it</h3>
            <p className="text-xs text-slate-500">Immobili medio termine inviati in automatico (sweep ogni 5 min + allineamento notturno 04:40)</p>
          </div>
        </div>
        <span data-testid="hometob-sync-enabled" className={`text-xs font-bold px-3 py-1 rounded-full ${status.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
          {status.enabled ? 'ATTIVA' : 'SPENTA'}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
          <p className="text-xs text-slate-500">Immobili sincronizzati</p>
          <p className="text-xl font-bold text-slate-900" data-testid="hometob-synced">{status.synced}/{status.eligible}</p>
        </div>
        <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
          <p className="text-xs text-slate-500">Invii 7 giorni</p>
          <p className="text-xl font-bold text-emerald-700" data-testid="hometob-delivered">{t.delivered ?? 0} ✓</p>
        </div>
        <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
          <p className="text-xs text-slate-500">Falliti 7 giorni</p>
          <p className={`text-xl font-bold ${t.failed ? 'text-red-600' : 'text-slate-900'}`} data-testid="hometob-failed">{t.failed ?? 0}</p>
        </div>
        <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
          <p className="text-xs text-slate-500">Ultimo allineamento</p>
          <p className="text-sm font-bold text-slate-900" data-testid="hometob-alignment">
            {status.last_alignment
              ? `${status.last_alignment.aligned}/${status.last_alignment.ours} identiche`
              : status.inventory_configured ? 'mai eseguito' : 'inventario non pronto'}
          </p>
        </div>
      </div>

      {failures.length > 0 && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3" data-testid="hometob-failures">
          <p className="text-xs font-bold text-red-700 mb-1">Invii falliti recenti:</p>
          {failures.slice(0, 5).map((f, i) => (
            <p key={i} className="text-xs text-red-700">
              {f.property_code || f.property_id} · {f.event} · {f.attempts} tentativi · {(f.error || '').slice(0, 60)}
            </p>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-3 mt-4">
        <Button size="sm" variant="outline" onClick={load} data-testid="hometob-refresh-btn">
          <RefreshCw className="h-4 w-4 mr-1" /> Aggiorna
        </Button>
        <Button size="sm" className="bg-indigo-900 hover:bg-indigo-800" onClick={pushAll} disabled={busy === 'push'} data-testid="hometob-pushall-btn">
          {busy === 'push' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} Reinvia tutto
        </Button>
        <Button size="sm" variant="outline" onClick={checkAlignment} disabled={busy === 'align'} data-testid="hometob-alignment-btn">
          {busy === 'align' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1" />} Verifica allineamento
        </Button>
      </div>
    </Card>
  );
};

export default HometobSyncPanel;
